import { buildReport, type PriceTable } from "@tktuner/core";
import pc from "picocolors";
import { loadPriceTable } from "../lib/prices-io";
import { setColors } from "../render/colors";
import { renderDebugSession, renderReport } from "../render/terminal";
import { openDb } from "../store/db";
import { Store } from "../store/queries";

export interface ReportOptions {
  days: string;
  all?: boolean;
  project?: string;
  json?: boolean;
  color?: boolean; // commander --no-color => color: false
  db?: string;
  prices?: string;
  debugSession?: string;
}

export async function report(opts: ReportOptions): Promise<void> {
  if (opts.color === false || opts.json) setColors(false);

  const days = opts.all ? null : Number(opts.days);
  if (days !== null && (!Number.isFinite(days) || days <= 0)) {
    console.error(pc.red(`Invalid --days value: ${opts.days}`));
    process.exitCode = 1;
    return;
  }

  const db = openDb(opts.db);
  try {
    const store = new Store(db);
    if (store.countTurns() === 0) {
      console.log(`No data yet — run ${pc.bold("tktuner scan")} first.`);
      return;
    }

    let prices: PriceTable;
    try {
      prices = loadPriceTable(opts.prices);
    } catch (err) {
      console.error(pc.red(`Could not load price override ${opts.prices}: ${String(err)}`));
      process.exitCode = 1;
      return;
    }

    const sinceTs = days === null ? undefined : Date.now() - days * 86400000;
    const turns = store.loadTurns({ sinceTs, projectLike: opts.project });
    if (turns.length === 0) {
      console.log(
        `No turns in the selected window${opts.project ? ` for project filter "${opts.project}"` : ""}. Try --all or a larger --days.`,
      );
      return;
    }
    const events = store.loadEvents();

    if (opts.debugSession) {
      console.log(renderDebugSession(turns, events, prices, opts.debugSession));
      return;
    }

    const model = buildReport(turns, events, prices, {
      days,
      project: opts.project ?? null,
    });
    if (opts.json) {
      console.log(JSON.stringify(model, null, 2));
    } else {
      console.log(renderReport(model));
    }
  } finally {
    db.close();
  }
}
